import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import MovementTimeline from '@/components/MovementTimeline';
import type { MovementState } from '@/components/MovementTimeline';

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get('/users/me');
      return data;
    },
  });

  const { data: rulesData } = useQuery({
    queryKey: ['rules-current'],
    queryFn: async () => {
      const { data } = await api.get('/rules/current');
      return data.rule;
    },
  });

  const balance = profile?.balance ?? '0.00';
  const movementState = profile?.movement_state;

  return (
    <>
      <Helmet>
        <title>Dashboard — React-City</title>
      </Helmet>

      <div className="space-y-6 animate-in">
        <div>
          <h1 className="text-2xl font-bold text-heading">
            Ciao, {user?.full_name ?? 'Utente'} 👋
          </h1>
          <p className="text-muted text-sm mt-1">
            Panoramica del tuo percorso nel Movimento del Dono
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Stato</p>
            <p className="text-2xl font-bold text-primary-500 dark:text-primary-400 mt-1">
              {isLoading ? '...' : movementState?.role ?? 'Nessun box'}
            </p>
          </div>
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Box attivo</p>
            <p className="text-2xl font-bold text-heading mt-1">
              {movementState?.box_id ? `#${movementState.box_id.slice(0, 6)}` : '—'}
            </p>
          </div>
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Saldo Ledger</p>
            <p className="text-2xl font-bold text-heading mt-1">€ {balance}</p>
          </div>
          <div className="card">
            <p className="text-xs font-medium text-muted uppercase tracking-wider">Payout previsto</p>
            <p className="text-2xl font-bold text-accent-500 dark:text-accent-400 mt-1">
              € {rulesData ? (rulesData.donation_amount * rulesData.pct_receiver / 100 * (rulesData.box_size - 1 - rulesData.n_bridges)).toFixed(0) : '...'}
            </p>
          </div>
        </div>

        {/* Regole attive */}
        {rulesData && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4 text-heading">Regole attive (v{rulesData.version})</h2>
            <div className="grid sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted">Box size</p>
                <p className="font-semibold">{rulesData.box_size} membri</p>
              </div>
              <div>
                <p className="text-muted">Donazione</p>
                <p className="font-semibold">€ {rulesData.donation_amount}</p>
              </div>
              <div>
                <p className="text-muted">% Receiver</p>
                <p className="font-semibold">{rulesData.pct_receiver}%</p>
              </div>
              <div>
                <p className="text-muted">% Fee / Fund</p>
                <p className="font-semibold">{rulesData.pct_fee}% / {rulesData.pct_fund}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Percorso nel Movimento */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-heading">Il tuo percorso</h2>
          {movementState?.role ? (
            <MovementTimeline
              currentState={(movementState.role as string).toUpperCase() as MovementState}
            />
          ) : (
            <div className="text-muted text-sm text-center py-6">
              <span className="text-3xl mb-2 block">�️</span>
              <p>Non hai ancora iniziato il percorso.</p>
              <Link to="/join" className="btn-primary mt-4 inline-flex no-underline">
                Entra nel Movimento
              </Link>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-heading">Azioni rapide</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/boxes" className="btn-primary no-underline">📦 I miei City-Box</Link>
            <Link to="/ledger" className="btn-secondary no-underline">💶 Ledger</Link>
            <Link to="/profile" className="btn-secondary no-underline">👤 Profilo</Link>
          </div>
        </div>

        {/* Attività recenti + Timeline compatta */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-heading">Attività recenti</h2>
          {movementState?.role ? (
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-muted">Avanzamento:</span>
              <MovementTimeline
                currentState={(movementState.role as string).toUpperCase() as MovementState}
                compact
              />
            </div>
          ) : null}
          <div className="text-muted text-sm text-center py-8">
            Nessuna attività recente. Entra nel tuo primo City-Box per iniziare!
          </div>
        </div>
      </div>
    </>
  );
}
